import React, { useState, useEffect } from 'react';
import { dressService, Dress } from '../services/dress';
import { designerService, Designer } from '../services/designer';
import DressModal from '../components/DressModal';
import AuthenticatedImage from '../components/AuthenticatedImage';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';
import { formatPrice } from '../utils/currency';

const Dresses: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [dresses, setDresses] = useState<Dress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDress, setEditingDress] = useState<Dress | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [updating, setUpdating] = useState<string | null>(null);
  const [selectedDesignerIds, setSelectedDesignerIds] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [tierFilter, setTierFilter] = useState<'all' | 'new' | 'paid_deposit' | 'exclusive' | 'partner'>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [styleMatcherFilter, setStyleMatcherFilter] = useState<'all' | 'included' | 'excluded'>('all');
  const [highlightedDressId, setHighlightedDressId] = useState<string | null>(null);

  useEffect(() => {
    loadDresses();
  }, [currentPage, searchTerm, selectedDesignerIds, tierFilter, activeFilter, styleMatcherFilter]);

  useEffect(() => {
    loadDesigners();
  }, []);

  const loadDresses = async () => {
    try {
      setLoading(true);
      // Load more items per page for client-side filtering
      const response = await dressService.getDresses({
        page: 1,
        limit: 1000, // Get all dresses for client-side filtering
        search: searchTerm || undefined,
        designerIds: selectedDesignerIds.length > 0 ? selectedDesignerIds.join(',') : undefined,
      });
      
      // Apply client-side filters
      let filteredDresses = response.data;
      
      // Filter by tier
      if (tierFilter !== 'all') {
        filteredDresses = filteredDresses.filter(dress => dress.tiers && dress.tiers.includes(tierFilter));
      }
      
      // Filter by active status
      if (activeFilter !== 'all') {
        const isActive = activeFilter === 'active';
        filteredDresses = filteredDresses.filter(dress => dress.isActive === isActive);
      }
      
      // Filter by style matcher status
      if (styleMatcherFilter !== 'all') {
        const isIncluded = styleMatcherFilter === 'included';
        filteredDresses = filteredDresses.filter(dress => dress.includeInStyleMatcher === isIncluded);
      }
      
      // Apply pagination to filtered results
      const itemsPerPage = 10;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedDresses = filteredDresses.slice(startIndex, endIndex);
      
      setDresses(paginatedDresses);
      setTotalPages(Math.ceil(filteredDresses.length / itemsPerPage));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load dresses');
    } finally {
      setLoading(false);
    }
  };

  const handleStyleMatcherToggle = async (dress: Dress) => {
    try {
      const updatedDress = await dressService.updateStyleMatcherStatus(
        dress.id,
        !dress.includeInStyleMatcher
      );
      setDresses(dresses.map(d => d.id === dress.id ? updatedDress : d));
      showSuccess(
        `Style Matcher ${updatedDress.includeInStyleMatcher ? 'enabled' : 'disabled'}`,
        `"${dress.name}" ${updatedDress.includeInStyleMatcher ? 'will now appear' : 'will no longer appear'} in Style Matcher recommendations`
      );
    } catch (err: any) {
      showError('Failed to update dress', err.response?.data?.message || 'Please try again');
    }
  };

  const handleActiveToggle = async (dress: Dress) => {
    if (updating === dress.id) return; // Prevent double clicks
    
    try {
      setUpdating(dress.id);
      setError(''); // Clear any previous errors
      console.log('Toggling dress active status:', dress.id, 'from', dress.isActive, 'to', !dress.isActive);
      
      const updatedDress = await dressService.updateDress(dress.id, { isActive: !dress.isActive });
      console.log('Updated dress:', updatedDress);
      
      setDresses(dresses.map(d => d.id === dress.id ? updatedDress : d));
      showSuccess(
        `Dress ${updatedDress.isActive ? 'activated' : 'deactivated'}`,
        `"${dress.name}" is now ${updatedDress.isActive ? 'visible' : 'hidden'} in the customer catalog`
      );
    } catch (err: any) {
      console.error('Error updating dress status:', err);
      showError('Failed to update dress status', err.response?.data?.message || 'Please try again');
    } finally {
      setUpdating(null);
    }
  };

  const loadDesigners = async () => {
    try {
      const response = await designerService.getDesigners();
      setDesigners(response.data);
    } catch (err) {
      console.error('Failed to load designers:', err);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadDresses();
  };

  const handleDesignerToggle = (designerId: string) => {
    setSelectedDesignerIds(prev => {
      if (prev.includes(designerId)) {
        return prev.filter(id => id !== designerId);
      } else {
        return [...prev, designerId];
      }
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedDesignerIds([]);
    setTierFilter('all');
    setActiveFilter('all');
    setStyleMatcherFilter('all');
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setEditingDress(null);
    setIsModalOpen(true);
  };

  const handleEdit = (dress: Dress) => {
    setEditingDress(dress);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingDress(null);
  };

  const handleModalSave = async (savedDress?: Dress) => {
    await loadDresses();
    handleModalClose();
    
    // Show success message
    if (editingDress) {
      showSuccess('Dress updated successfully', `"${editingDress.name}" has been updated`);
    } else {
      showSuccess('Dress created successfully', 'The new dress has been added to the catalog');
    }
    
    // Highlight the updated/created dress
    if (savedDress?.id) {
      setHighlightedDressId(savedDress.id);
      setTimeout(() => setHighlightedDressId(null), 2000);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const dress = dresses.find(d => d.id === id);
      await dressService.deleteDress(id);
      await loadDresses();
      setDeleteConfirm(null);
      showSuccess('Dress deleted successfully', dress ? `"${dress.name}" has been removed` : 'The dress has been removed');
    } catch (err: any) {
      showError('Failed to delete dress', err.response?.data?.message || 'Please try again');
      setDeleteConfirm(null);
    }
  };

  if (loading && dresses.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading dresses...</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Dress Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage dress inventory and Style Matcher inclusion settings.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={handleCreate}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add Dress
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="mt-6">
        <form onSubmit={handleSearch} className="mb-4">
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Search dresses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FunnelIcon className="h-5 w-5 mr-2" />
              Filters
              {(selectedDesignerIds.length > 0 || tierFilter !== 'all' || activeFilter !== 'all' || styleMatcherFilter !== 'all') && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-indigo-600 rounded-full">
                  {[
                    selectedDesignerIds.length > 0 ? 1 : 0,
                    tierFilter !== 'all' ? 1 : 0,
                    activeFilter !== 'all' ? 1 : 0,
                    styleMatcherFilter !== 'all' ? 1 : 0
                  ].reduce((a, b) => a + b, 0)}
                </span>
              )}
            </button>
          </div>
        </form>

        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900">Filters</h3>
              {(selectedDesignerIds.length > 0 || tierFilter !== 'all' || activeFilter !== 'all' || styleMatcherFilter !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                  Clear all
                </button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
              {/* Designer Filter */}
              <div className="lg:col-span-5">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Designer</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-32 overflow-y-auto">
                  {designers.map((designer) => (
                    <label
                      key={designer.id}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDesignerIds.includes(designer.id)}
                        onChange={() => handleDesignerToggle(designer.id)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 truncate">{designer.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tier Filter */}
              <div className="lg:col-span-3">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Access Level</h4>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tierFilter"
                      value="all"
                      checked={tierFilter === 'all'}
                      onChange={() => setTierFilter('all')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">All</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tierFilter"
                      value="new"
                      checked={tierFilter === 'new'}
                      onChange={() => setTierFilter('new')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">New</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tierFilter"
                      value="paid_deposit"
                      checked={tierFilter === 'paid_deposit'}
                      onChange={() => setTierFilter('paid_deposit')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Paid Deposit</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tierFilter"
                      value="exclusive"
                      checked={tierFilter === 'exclusive'}
                      onChange={() => setTierFilter('exclusive')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Exclusive</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tierFilter"
                      value="partner"
                      checked={tierFilter === 'partner'}
                      onChange={() => setTierFilter('partner')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Partner</span>
                  </label>
                </div>
              </div>

              {/* Active Status Filter */}
              <div className="lg:col-span-2">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Status</h4>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="activeFilter"
                      value="all"
                      checked={activeFilter === 'all'}
                      onChange={() => setActiveFilter('all')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">All</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="activeFilter"
                      value="active"
                      checked={activeFilter === 'active'}
                      onChange={() => setActiveFilter('active')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="activeFilter"
                      value="inactive"
                      checked={activeFilter === 'inactive'}
                      onChange={() => setActiveFilter('inactive')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
              </div>

              {/* Style Matcher Filter */}
              <div className="lg:col-span-2">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Style Matcher</h4>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="styleMatcherFilter"
                      value="all"
                      checked={styleMatcherFilter === 'all'}
                      onChange={() => setStyleMatcherFilter('all')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">All</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="styleMatcherFilter"
                      value="included"
                      checked={styleMatcherFilter === 'included'}
                      onChange={() => setStyleMatcherFilter('included')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Included</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="styleMatcherFilter"
                      value="excluded"
                      checked={styleMatcherFilter === 'excluded'}
                      onChange={() => setStyleMatcherFilter('excluded')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Excluded</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {dresses.map((dress) => (
              <li 
                key={dress.id} 
                className={`px-6 py-4 transition-all duration-500 ${
                  highlightedDressId === dress.id ? 'bg-green-50 ring-2 ring-green-500 ring-opacity-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-start">
                    <div 
                      className="flex-shrink-0 w-24 mr-4 cursor-pointer"
                      onClick={() => handleEdit(dress)}
                    >
                      {dress.thumbnailUrl ? (
                        <AuthenticatedImage
                          src={dress.thumbnailUrl}
                          alt={dress.name}
                          className="w-24 object-contain rounded-md hover:opacity-80 transition-opacity"
                          fallback={
                            <div className="w-24 h-24 bg-gray-200 rounded-md flex items-center justify-center hover:bg-gray-300 transition-colors">
                              <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          }
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gray-200 rounded-md flex items-center justify-center hover:bg-gray-300 transition-colors">
                          <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 
                        className="text-lg font-medium text-gray-900 truncate cursor-pointer hover:text-indigo-600 transition-colors"
                        onClick={() => handleEdit(dress)}
                      >
                        {dress.name}
                      </h3>
                    {dress.description && (
                      <p className="mt-1 text-sm text-gray-600 truncate">
                        {dress.description}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center text-sm text-gray-500">
                      <span className="mr-4">Designer: {dress.designer?.name || 'Unknown'}</span>
                      {dress.launchDate && (
                        <span className="mr-4">Launch: {new Date(dress.launchDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      )}
                    </div>
                    {/* Per-store pricing, each in the store's own currency. */}
                    {dress.locations && dress.locations.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                        {dress.locations.map(loc => (
                          <span key={loc.id}>
                            <span className="font-medium text-gray-600">{loc.name}:</span>{' '}
                            Purchase {formatPrice(loc.DressLocation?.purchasePrice ?? dress.purchasePrice, loc.currency)}
                            {' · '}
                            Rental {formatPrice(loc.DressLocation?.rentalPrice ?? dress.rentalPrice, loc.currency)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-1 text-sm text-gray-400">No stores assigned</div>
                    )}
                    </div>
                  </div>
                  <div className="ml-4 flex items-center space-x-2">
                    {/* Tier Badges */}
                    <div className="flex items-center gap-1">
                      {dress.tiers && dress.tiers.length > 0 ? (
                        dress.tiers.map((tier) => (
                          <span
                            key={tier}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              tier === 'exclusive'
                                ? 'bg-purple-100 text-purple-800'
                                : tier === 'paid_deposit'
                                ? 'bg-blue-100 text-blue-800'
                                : tier === 'partner'
                                ? 'bg-teal-100 text-teal-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {tier === 'exclusive' ? 'Exclusive' : tier === 'paid_deposit' ? 'Paid Deposit' : tier === 'partner' ? 'Partner' : 'New'}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          No Tiers
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleActiveToggle(dress)}
                      disabled={updating === dress.id}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors duration-200 ${
                        updating === dress.id
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          : dress.isActive 
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {updating === dress.id ? 'Updating...' : (dress.isActive ? 'Active' : 'Inactive')}
                    </button>
                    <div className="flex items-center">
                      <label className="text-sm font-medium text-gray-700 mr-2">
                        Style Matcher
                      </label>
                      <button
                        onClick={() => handleStyleMatcherToggle(dress)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                          dress.includeInStyleMatcher ? 'bg-indigo-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            dress.includeInStyleMatcher ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <button
                      onClick={() => handleEdit(dress)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Edit
                    </button>
                    {deleteConfirm === dress.id ? (
                      <>
                        <button
                          onClick={() => handleDelete(dress.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(dress.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center">
              <p className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <DressModal
          dress={editingDress}
          designers={designers}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default Dresses;